/**
 * F&P custom-card payment form.
 *
 * Renders:
 *  - holder-name input (PCI out-of-scope; no card data touches it)
 *  - Adyen SecuredFields for card number / expiry / CVC (mounted via
 *    `lib/adyenCheckout.ts`; data-cse placeholders below are where the
 *    encrypted iframes attach)
 *  - country-aware terms checkbox
 *  - optional subscription-consent checkbox (cart has a subscription line item
 *    + non-UK)
 *  - submit button whose copy depends on `mode`:
 *      'pay'      → "Pay" — used on /checkout
 *      'tokenize' → "NEXT" — used on /save-card
 *
 * The form is presentational — the parent owns the Adyen session creation
 * and the onSubmit side effects (POST /confirm or POST /save).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

import LoadingSpinner from '../common/LoadingSpinner';
import { createAdyenCheckout, mountSecuredFields } from '../../lib/adyenCheckout';
import {
  createAdyenCheckoutAdvanced,
  mountSecuredFieldsAdvanced,
  type AdvancedSecuredFieldsHandle,
} from '../../lib/adyenCheckout';
import type {
  AdyenAdvancedActions,
  AdyenAdvancedInitOptions,
  AdyenAuthoriseAdditionalData,
  AdyenAuthoriseSubmitData,
  AdyenCheckoutInitOptions,
  AdyenPaymentCompletedResult,
  AdyenSecuredFieldsState,
  PaymentFormMode,
} from '../../types/adyen';
import {
  COUNTRY_CODES,
  ERROR_MESSAGES,
  UI_CONFIG,
} from '../../utils/constants';

import HolderNameInput from './HolderNameInput';
import SubscriptionConsent from './SubscriptionConsent';
import SubscriptionFootnote from './SubscriptionFootnote';
import TermsCheckbox from './TermsCheckbox';

/**
 * Minimum time (ms) the Adyen init loader stays on screen. Adyen usually mounts
 * the secured fields in <100ms once cached, so without a floor the loader would
 * flash for a single frame and look like nothing happened.
 */
const MIN_INIT_LOADER_MS = 500;

export interface PaymentFormProps {
  /**
   * Session payload from the BFF (PaymentSessionResponse.adyenSession{Id,Data}).
   * Required for `mode='pay'` (Sessions flow). Omitted for `mode='tokenize'`,
   * which uses the Advanced (no-session) flow.
   */
  session?: { id: string; sessionData: string };
  clientKey: string;
  environment: 'test' | 'live' | 'live-au';
  country: string;
  /** 'pay' (charge now) | 'tokenize' (zero-auth save card). */
  mode: PaymentFormMode;
  /**
   * Whether the cart contains a subscription line item (any item with
   * `purchaseType === 'subscription'`). Drives the subscription-consent
   * checkbox and footnote.
   */
  hasSubscriptionItem?: boolean;
  /**
   * `mode='pay'` — Adyen onPaymentCompleted. Parent forwards the Adyen result +
   * holder name to the BFF `/confirm`.
   */
  onPaymentCompleted?: (
    result: AdyenPaymentCompletedResult,
    extras: { holderName: string; brand?: string; encryptedSecurityCode?: string },
  ) => void;
  /**
   * `mode='tokenize'` — fired when the shopper submits the Advanced card form.
   * Carries the CSE-encrypted card blob + browserInfo (+ the holder name typed
   * into our own input). Parent POSTs this to the BFF `/authorise`.
   */
  onAuthoriseSubmit?: (data: AdyenAuthoriseSubmitData, actions: AdyenAdvancedActions) => void;
  /**
   * `mode='tokenize'` — fired after a 3DS2 challenge resolves. Parent POSTs the
   * `details` to the BFF `/authorise-details`.
   */
  onAuthoriseAdditionalDetails?: (
    data: AdyenAuthoriseAdditionalData,
    actions: AdyenAdvancedActions,
  ) => void;
  /**
   * Optional advanced handle callback kept for compatibility with existing
   * callers; the save-card flow now uses Adyen callback actions for 3DS2.
   */
  onAdvancedReady?: (handle: AdvancedSecuredFieldsHandle) => void;
  /** Hard SDK errors (iframe load, network) bubble up here. */
  onError: (error: Error) => void;
  /** Notifies parent when submit handoff is in-flight before 3DS challenge mounts. */
  onSubmitInFlightChange?: (inFlight: boolean) => void;
}

const ErrorText: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography sx={{ color: UI_CONFIG.ERROR_COLOR }}>{label}</Typography>
    <ErrorIcon fontSize="small" sx={{ fontSize: '14px' }} color="error" />
  </Box>
);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ color: UI_CONFIG.TEXT_COLOR }}>{children}</Typography>
);

const PaymentForm: React.FC<PaymentFormProps> = ({
  session,
  clientKey,
  environment,
  country,
  mode,
  hasSubscriptionItem = false,
  onPaymentCompleted,
  onAuthoriseSubmit,
  onAuthoriseAdditionalDetails,
  onAdvancedReady,
  onError,
  onSubmitInFlightChange,
}) => {
  const paymentContainerRef = useRef<HTMLDivElement | null>(null);
  const adyenHandleRef = useRef<import('../../lib/adyenCheckout').SecuredFieldsHandle | null>(null);
  const [holderName, setHolderName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedSubscription, setAcceptedSubscription] = useState(false);
  const [fieldsState, setFieldsState] = useState<AdyenSecuredFieldsState | null>(null);
  const [isThreeDSChallengeVisible, setIsThreeDSChallengeVisible] = useState(false);
  const [isSubmitInFlight, setIsSubmitInFlight] = useState(false);
  // True once a 3DS2 challenge has been shown and then closed. Adyen is
  // finalising the result and the parent is about to navigate to the status
  // page; keep the shopper inputs hidden and show a loader so the card fields
  // don't flash back on screen during the redirect.
  const [isFinalizing, setIsFinalizing] = useState(false);
  const challengeShownRef = useRef(false);
  // True until the Adyen secured-field iframes have mounted. The parent's shell
  // loader has already cleared by the time this form renders (session + cart are
  // ready), so without this the empty card fields flash before Adyen finishes
  // initialising.
  const [isInitializing, setIsInitializing] = useState(true);
  // Adyen's SDK + secured-field iframes often mount in well under 100ms (the
  // web component and assets are cached after the first load). That made the
  // init loader flash for a single frame and appear "not to show" at all. Keep
  // it up for a short minimum so the transition reads as a deliberate loader
  // rather than a flicker. `initStartRef` records when the loader was shown and
  // `initClearScheduledRef` guards against scheduling the hide more than once
  // (the MutationObserver below can fire many times as Adyen builds the DOM).
  const initStartRef = useRef(0);
  const initClearScheduledRef = useRef(false);

  // Refs so the SDK's `onPaymentCompleted` callback (captured once at mount)
  // always reads the LATEST holder name + BIN-detected brand. Without these,
  // submitting after typing in the holder-name field would send the empty
  // string that was captured at mount time.
  const holderNameRef = useRef(holderName);
  const fieldsStateRef = useRef(fieldsState);
  useEffect(() => {
    holderNameRef.current = holderName;
  }, [holderName]);
  useEffect(() => {
    fieldsStateRef.current = fieldsState;
  }, [fieldsState]);

  // When Adyen switches from card-entry to the native 3DS2 challenge, hide the
  // shopper-input surfaces (holder name, terms, submit) and keep only the
  // challenge UI visible.
  useEffect(() => {
    const container = paymentContainerRef.current;
    if (!container) return;

    const updateVisibility = (): void => {
      const hasChallenge = Boolean(
        container.querySelector(
          '.adyen-checkout__threeds2__challenge, .adyen-checkout__iframe--threeDSIframe',
        ),
      );
      // Drop the init loader only once Adyen has actually injected the secured
      // field iframes into the container. Clearing it the moment `mount()`
      // returns (as we did before) hid the loader almost instantly, before the
      // fields were visible, leaving a blank gap. `querySelector('iframe')` is a
      // reliable readiness signal here — at init the only iframes are the
      // secured fields (the 3DS iframe appears later, when init is already done).
      // Enforce MIN_INIT_LOADER_MS so a sub-100ms Adyen mount still shows a
      // perceptible loader instead of a one-frame flash.
      if (!initClearScheduledRef.current && container.querySelector('iframe')) {
        initClearScheduledRef.current = true;
        const elapsed = performance.now() - initStartRef.current;
        const remaining = Math.max(0, MIN_INIT_LOADER_MS - elapsed);
        window.setTimeout(() => setIsInitializing(false), remaining);
      }
      // Update all challenge-driven state in ONE callback so React batches the
      // renders. Setting `isFinalizing` in a separate effect (reacting to
      // `isThreeDSChallengeVisible`) caused an intermediate render where the
      // challenge was gone but finalizing was not yet set, flashing the card
      // form for a frame.
      setIsThreeDSChallengeVisible(hasChallenge);
      if (hasChallenge) {
        challengeShownRef.current = true;
        setIsSubmitInFlight(false);
        onSubmitInFlightChange?.(false);
      } else if (challengeShownRef.current) {
        // Challenge was shown and has now closed — Adyen is finalising and the
        // parent will navigate. Keep the inputs hidden and reuse the parent's
        // overlay loader (same spinner + size as the initial submit).
        setIsFinalizing(true);
        onSubmitInFlightChange?.(true);
      }
    };

    updateVisibility();

    const observer = new MutationObserver(() => updateVisibility());
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.sessionData, clientKey, environment, country, mode]);

  // Adyen SDK lifecycle — mount once on (session, clientKey, environment) change.
  useEffect(() => {
    if (!paymentContainerRef.current) return;

    let cancelled = false;
    let unmount: (() => void) | null = null;

    const mountAdvanced = async (): Promise<void> => {
      // Advanced (no-session) tokenize flow. `onSubmit` hands the encrypted
      // card + browserInfo to the parent, which relays it to the BFF
      // `/authorise`. The parent resolves callback actions with server responses,
      // and any 3DS2 continuation surfaces through `onAdditionalDetails`.
      const init: AdyenAdvancedInitOptions = {
        clientKey,
        environment,
        countryCode: country?.toUpperCase(),
        onSubmit: (data, actions) =>
          onAuthoriseSubmit?.({
            paymentMethod: {
              ...data.paymentMethod,
              ...(holderNameRef.current ? { holderName: holderNameRef.current } : {}),
            },
            ...(data.browserInfo ? { browserInfo: data.browserInfo } : {}),
          }, actions),
        onAdditionalDetails: (data, actions) => onAuthoriseAdditionalDetails?.(data, actions),
        onError,
      };
      const checkout = await createAdyenCheckoutAdvanced(init);
      if (cancelled || !paymentContainerRef.current) return;
      const handle = mountSecuredFieldsAdvanced(checkout, {
        container: paymentContainerRef.current,
        onChange: (state) => setFieldsState(state),
        onError,
      });
      adyenHandleRef.current = handle;
      onAdvancedReady?.(handle);
      unmount = handle.unmount;
      // Note: the init loader is cleared by the MutationObserver once the
      // secured-field iframes actually appear — not here, because mount()
      // returning does not mean the fields have rendered yet.
    };

    const mountSessions = async (): Promise<void> => {
      if (!session) return;
      const init: AdyenCheckoutInitOptions = {
        session,
        clientKey,
        environment,
        countryCode: country?.toUpperCase(),
        onPaymentCompleted: (result) =>
          onPaymentCompleted?.(result, {
            holderName: holderNameRef.current,
            brand: fieldsStateRef.current?.brand,
            encryptedSecurityCode:
              fieldsStateRef.current?.data?.paymentMethod?.encryptedSecurityCode,
          }),
        onError,
      };
      const checkout = await createAdyenCheckout(init);
      if (cancelled || !paymentContainerRef.current) return;
      const handle = mountSecuredFields(checkout, {
        container: paymentContainerRef.current,
        onChange: (state) => setFieldsState(state),
        onError,
      });
      adyenHandleRef.current = handle;
      unmount = handle.unmount;
      // Note: the init loader is cleared by the MutationObserver once the
      // secured-field iframes actually appear — not here, because mount()
      // returning does not mean the fields have rendered yet.
    };

    void (async () => {
      // Show a loader while the Adyen SDK loads and the secured-field iframes
      // mount. The parent shell loader has already cleared by now, so this local
      // overlay covers the init gap (same spinner + size as submit/finalise).
      initStartRef.current = performance.now();
      initClearScheduledRef.current = false;
      setIsInitializing(true);
      try {
        if (mode === 'tokenize') {
          await mountAdvanced();
        } else {
          await mountSessions();
        }
      } catch (error) {
        if (!cancelled) {
          setIsInitializing(false);
          onError(error instanceof Error ? error : new Error('Adyen init failed'));
        }
      }
    })();

    return () => {
      onSubmitInFlightChange?.(false);
      cancelled = true;
      adyenHandleRef.current = null;
      unmount?.();
    };
    // `holderName` / `fieldsState` are intentionally NOT in deps — the SDK
    // is mounted once per session and reads the latest values via refs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.sessionData, clientKey, environment, country, mode]);

  // Subscription surfaces are driven purely by the cart: show them whenever the
  // cart contains a subscription line item (`hasSubscriptionItem`). The consent
  // checkbox is additionally suppressed for UK (which has no explicit consent
  // checkbox); the footnote still renders for UK but with its info icon and
  // cancel-instructions line suppressed (handled inside SubscriptionFootnote).
  const showSubscriptionConsent =
    hasSubscriptionItem && country?.toUpperCase() !== COUNTRY_CODES.UK;

  const showSubscriptionFootnote = hasSubscriptionItem;

  // The button is disabled until every required shopper-facing surface is
  // satisfied: terms accepted, subscription consent (when shown), AND all
  // Adyen card fields reporting valid (`fieldsState.isValid`). The cardholder
  // name is OPTIONAL and does not gate submission. `fieldsState` starts `null`,
  // so the button is disabled on first render and only enables once Adyen
  // confirms the encrypted fields are valid; any field error flips `isValid`
  // back to false and re-disables it. `isSubmitInFlight` is included so the
  // `disabled` prop reflects the in-flight state immediately, as a second line
  // of defence alongside the synchronous ref guard in `handleSubmit` below —
  // the shopper-input Box (button included) is also hidden once submit starts
  // (`hideShopperInputs`), but that hide only takes effect after React
  // re-renders, which is not fast enough to stop a rapid double-click/tap.
  const submitDisabled =
    !acceptedTerms ||
    (showSubscriptionConsent && !acceptedSubscription) ||
    !fieldsState?.isValid ||
    isSubmitInFlight;

  // Synchronous guard against double-submits. A fast double-click/tap can
  // dispatch two click events before React commits the `isSubmitInFlight`
  // state update (and the resulting `disabled`/hidden button), which would
  // otherwise let `adyenHandleRef.current?.submit()` fire twice. Checking and
  // flipping this ref at the very top of `handleSubmit` — before any state
  // updates — disables the button for all practical purposes the instant it
  // is clicked, regardless of render timing. Reset happens on remount (the
  // parent bumps `key` to fully reset the form on 3DS cancel/retry).
  const isSubmittingRef = useRef(false);

  const handleSubmit = (): void => {
    if (submitDisabled || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitInFlight(true);
    onSubmitInFlightChange?.(true);
    // Delegate to the Adyen SDK — it will POST the encrypted card data to
    // Adyen's Sessions API. When Adyen responds, the `onPaymentCompleted`
    // callback configured in `createAdyenCheckout` fires. The Sessions flow
    // does NOT surface `recurringDetailReference` / last4 / expiry to the
    // browser — those live in Adyen's `/payments` response. The parent
    // forwards what we DO have (holder name + BIN-detected brand) to the
    // BFF, and Medusa fetches the rest from Adyen `/storedPaymentMethods`.
    adyenHandleRef.current?.submit();
  };

  const buttonCopy = mode === 'pay' ? 'Pay' : 'NEXT';
  const hideShopperInputs =
    isInitializing || isThreeDSChallengeVisible || isSubmitInFlight || isFinalizing;

  return (
    <Box
      className="payment adyen-checkout__field"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        // While the shopper inputs are hidden (init / submit / 3DS finalise), the
        // content collapses to near-zero height. Keep a minimum height so the
        // overlay loader has room to render without being clipped.
        ...(hideShopperInputs ? { minHeight: '300px' } : {}),
      }}
    >
      {/* Local loader for the Adyen init gap. A fixed-height centered box (not
          an overlay) so it always has room and can't collapse to zero height.
          Same LoadingSpinner + medium size as the parent shell loader. */}
      {isInitializing && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
          }}
        >
          <LoadingSpinner isLoading size="medium" />
        </Box>
      )}

      <Box sx={{ display: hideShopperInputs ? 'none' : 'block' }}>
        <HolderNameInput value={holderName} onChange={setHolderName} />
      </Box>

      {/*
        Adyen v6 SecuredFields/CustomCard mounts the encrypted iframes onto
        ANY descendant of the container element that has the matching
        `data-cse` attribute. The ref must therefore wrap ALL THREE
        placeholders — `encryptedCardNumber`, `encryptedExpiryDate`, and
        `encryptedSecurityCode`. Previously the ref was on a Box that only
        contained `encryptedCardNumber`, so Adyen never found the expiry
        and CVC placeholders and the user could not type into those fields.
      */}
      <Box ref={paymentContainerRef}>
        <Box sx={{ display: hideShopperInputs ? 'none' : 'block' }}>
          <Box className="input-gap">
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <FieldLabel>Card number</FieldLabel>
              {fieldsState?.errors?.encryptedCardNumber && (
                <ErrorText label={ERROR_MESSAGES.CARD_NUMBER} />
              )}
            </Box>
            <span
              className="adyen-checkout__input adyen-checkout__card__cardNumber__input"
              data-cse="encryptedCardNumber"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 4, width: '100%' }}>
            <Box className="input-gap" sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <FieldLabel>Expiry date</FieldLabel>
                {fieldsState?.errors?.encryptedExpiryDate && (
                  <ErrorText label={ERROR_MESSAGES.EXPIRY_DATE} />
                )}
              </Box>
              <span className="adyen-checkout__input" data-cse="encryptedExpiryDate" />
            </Box>

            <Box className="input-gap" sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <FieldLabel>CVC/CVV</FieldLabel>
                {fieldsState?.errors?.encryptedSecurityCode && <ErrorText label="" />}
              </Box>
              <span className="adyen-checkout__input" data-cse="encryptedSecurityCode" />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: hideShopperInputs ? 'none' : 'block' }}>
        <TermsCheckbox checked={acceptedTerms} onChange={setAcceptedTerms} country={country} />
        {showSubscriptionConsent && (
          <Box sx={{ mt: 2 }}>
            <SubscriptionConsent
              checked={acceptedSubscription}
              onChange={setAcceptedSubscription}
              country={country}
            />
          </Box>
        )}
        {showSubscriptionFootnote && <SubscriptionFootnote country={country} />}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          sx={{
            mt: 2,
            // Match the legacy `.button` design (index.css): full-width, taller
            // (15px padding), 6px radius, normal-case 1em text — MUI's defaults
            // (short 36px height, uppercased ~14px, 4px radius) looked off here.
            width: '100%',
            padding: '15px',
            borderRadius: '6px',
            fontSize: '1em',
            fontWeight: 500,
            lineHeight: 1.2,
            textTransform: 'none',
            background: UI_CONFIG.PRIMARY_COLOR,
            ':hover': { background: UI_CONFIG.PRIMARY_HOVER_COLOR },
            '&.Mui-disabled': {
              background: UI_CONFIG.DISABLED_BACKGROUND,
              color: UI_CONFIG.DISABLED_COLOR,
            },
          }}
        >
          {buttonCopy}
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentForm;
